import * as Yup from 'yup';
import { AutocompleteRenderOptionState, Box } from '@mui/material';
import { ChipAutocomplete, PoolSpinner } from '@synergy/components';
import { Field, FieldProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { Locale, TopicListItem } from '@synergy/models';
import { PaloField, PaloFormGrid, PaloFormSection } from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PoolTypography } from '@synergy/components-next';
import { SyntheticEvent, useEffect, useState } from 'react';
import { errorsToTouch, fields, getLocalizedString } from '@synergy/utils';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { ContactCountPerTopicAndRegionTable } from '../pages/components/contact-count-per-topic-and-region-table';
import { DemandContactTopicRegionCount } from '../../../models/demand-contact-topic-region-count';
import { DemandSteps } from '../models/demand-steps';
import { FormikGroupItem } from '../../../../../common/components/new-stepper/formik-group-item';
import { RegionWithId } from '../../../../../common/models/region';
import { TopicsFormValues } from '../models/topics-form-values';
import {
    contactCountPerTopicAndRegionSelector,
    demandDetailStepperStateSelector,
} from '../store/demand-detail-selectors';
import {
    demandDetailTopicsBaselineSelector,
    demandDetailTopicsDataSelector,
} from '../store/topics/demand-detail-topics-selectors';
import { fetchContactCountPerTopicAndRegion, submitTopics } from '../store/topics/demand-detail-topics-actions';
import { localesSelector } from '../../../../../common/selectors/locale-selectors';
import { regionSearchResultsSelector } from '../../../../../common/selectors/regions-selectors';
import { searchRegions } from '../../../../../common/actions/region-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const fieldNames = fields<TopicsFormValues>();

const validationSchema = Yup.object().shape({
    [fieldNames.topics]: Yup.array().required('app.error.required'),
    [fieldNames.regions]: Yup.array().required('app.error.required'),
});

export const PaloDemandDetailTopics = (): JSX.Element => {
    const dispatch = useThunkDispatch();
    const topicsStepData = useAppSelector(demandDetailTopicsDataSelector);
    // The step as the server has it — see FormikGroupItem.dirtyBaseline.
    const baseline = useAppSelector(demandDetailTopicsBaselineSelector);
    const topicOptions = useAppSelector(state => state.topics.topics.items);
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Topics);
    const locales: Locale[] = useAppSelector(localesSelector);
    const regionSearchResults: RegionWithId[] = useAppSelector(regionSearchResultsSelector);
    const contactCountPerTopicAndRegion: DemandContactTopicRegionCount[] = useAppSelector(
        contactCountPerTopicAndRegionSelector
    );
    const [isLoading, setIsLoading] = useState(true);
    const totalResultCount = contactCountPerTopicAndRegion.reduce((result, item) => result + item.count, 0);
    const countryCodeFilter = ['AT', 'DE'];

    useEffect(() => {
        dispatch(searchRegions({ countryCodes: countryCodeFilter }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A closed survey kept its filter but asked for nothing, so the step said
    // that no results were found and that a topic should be added — with the
    // topic sitting right above the sentence.
    useEffect(() => {
        const topicIds = topicsStepData.values.topics.map(topic => topic.id);
        const regionCodes = topicsStepData.values.regions.map(region => region.code);

        setIsLoading(true);
        dispatch(fetchContactCountPerTopicAndRegion(topicIds, regionCodes));
    }, [dispatch, topicsStepData.values.topics, topicsStepData.values.regions]);

    useEffect(() => {
        setIsLoading(false);
    }, [contactCountPerTopicAndRegion]);

    function handleContactCountsPerTopicAndRegionChange(topicIds: number[], regionCodes: string[]) {
        if (!readonly) {
            setIsLoading(true);
            dispatch(fetchContactCountPerTopicAndRegion(topicIds, regionCodes));
        }
    }

    const renderTopicOption = (topic: TopicListItem, state: AutocompleteRenderOptionState) => {
        const title = getLocalizedString(locales, topic.title);
        const nameMatches = match(title, state.inputValue);
        const nameParts = parse(title, nameMatches);

        return (
            <div key={topic.id}>
                {nameParts.map((namePart, index) => (
                    <Box
                        key={index}
                        component="span"
                        sx={{ color: namePart.highlight ? tokens.color.accent : undefined }}
                    >
                        {namePart.text}
                    </Box>
                ))}
            </div>
        );
    };

    const handleRegionSearch = (_event: SyntheticEvent<Element, Event>, value: string) => {
        dispatch(searchRegions({ search: value, countryCodes: countryCodeFilter }));
    };

    return (
        <PaloThemeProvider>
            <FormikGroupItem
                groupItemId={DemandSteps.Topics}
                dirtyBaseline={baseline}
                initialValues={topicsStepData.values}
                onSubmit={data => dispatch(submitTopics(data))}
                validationSchema={validationSchema}
                initialErrors={topicsStepData.errors}
                initialTouched={errorsToTouch(topicsStepData.errors)}
                enableReinitialize={true}
                isInitialValid={topicsStepData.isValid}
            >
                <PaloFormSection title={<FormattedMessage id="app.demands.detail.topics.selectFilter" />}>
                    <PaloFormGrid columns={2}>
                        <PaloField label={<FormattedMessage id="app.demands.detail.topics.topics" />}>
                            <Field name={fieldNames.topics}>
                                {({ field, form }: FieldProps<TopicListItem[]>) => (
                                    <ChipAutocomplete
                                        value={field.value}
                                        noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                                        options={topicOptions}
                                        renderOption={renderTopicOption}
                                        getOptionLabel={(topic: TopicListItem) =>
                                            getLocalizedString(locales, topic.title)
                                        }
                                        getChipLabel={(topic: TopicListItem) =>
                                            getLocalizedString(locales, topic.title)
                                        }
                                        textfieldPlaceholder="app.select.placeholder.topics"
                                        onChange={(_event, value) => {
                                            form.setFieldValue(field.name, value);
                                            handleContactCountsPerTopicAndRegionChange(
                                                value.map(v => v.id),
                                                form
                                                    .getFieldProps(fieldNames.regions)
                                                    .value.map((v: RegionWithId) => v.code)
                                            );
                                        }}
                                        disabled={readonly}
                                    />
                                )}
                            </Field>
                        </PaloField>
                        <PaloField label={<FormattedMessage id="app.demands.detail.topics.regions" />}>
                            <Field name={fieldNames.regions}>
                                {({ field, form }: FieldProps<RegionWithId[]>) => (
                                    <ChipAutocomplete
                                        value={field.value}
                                        noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                                        options={regionSearchResults}
                                        getOptionLabel={region => `${region.name}`}
                                        getChipLabel={region => region.name}
                                        textfieldPlaceholder="app.select.placeholder.regions"
                                        onTextFieldValueChange={handleRegionSearch}
                                        onChange={(_event, value) => {
                                            form.setFieldValue(field.name, value);
                                            handleContactCountsPerTopicAndRegionChange(
                                                form
                                                    .getFieldProps(fieldNames.topics)
                                                    .value.map((v: TopicListItem) => v.id),
                                                value.map(v => v.code)
                                            );
                                        }}
                                        disabled={readonly}
                                    />
                                )}
                            </Field>
                        </PaloField>
                    </PaloFormGrid>
                </PaloFormSection>
            </FormikGroupItem>

            <PaloFormSection
                title={<FormattedMessage id="app.demands.detail.topics.results" values={{ count: totalResultCount }} />}
            >
                <PoolSpinner enabled={isLoading} sx={{ width: '100%' }}>
                    {contactCountPerTopicAndRegion.length > 0 ? (
                        <ContactCountPerTopicAndRegionTable data={contactCountPerTopicAndRegion} locales={locales} />
                    ) : (
                        !isLoading && (
                            <PoolTypography translationId="app.demands.detail.topics.noResults" typography="body1" />
                        )
                    )}
                </PoolSpinner>
            </PaloFormSection>
        </PaloThemeProvider>
    );
};
